import { ExclamationCircleOutlined } from '@ant-design/icons';
import { message, Modal } from 'antd';
import React, { type CSSProperties } from 'react';

import client from '@/lib/feathers';
import type { CommunityData } from '@/types/database/communityData';
import type CommunityGroups from '@/types/database/communityGroups';
import type { SubscriptionPlan } from '@/types/database/subscriptionPlan';
import type { User } from '@/types/database/users';
import {
  generateHouseHoldNumberCascader,
  type Option,
} from '@/utils/generateHouseHoldNumberCascader';

import type {
  ExtraUserInfoFormat,
  MembershipInformation,
  TableRecord,
} from './types';

const { confirm } = Modal;

export const showDeleteConfirm = (id: string, updateUsers: () => void) => {
  confirm({
    title: '確定要刪除該會員個資嗎？',
    icon: <ExclamationCircleOutlined />,
    content: '注意！刪除後無法復原，確定請按確認鍵',
    onOk: async () => {
      try {
        await client.service('users').remove(id);
        updateUsers();
        message.success('會員刪除成功!');
      } catch (error) {
        message.error('會員刪除失敗!');
      }
    },
  });
};

export const getMemberInfos = (
  users: (User & MembershipInformation & { _id: string })[]
) => {
  const memberInfos = users.map((user) => {
    const { userExtraInfo } = user;
    return {
      ...user,
      ...userExtraInfo,
    };
  });

  return memberInfos;
};

export const sortColumns = (a: ExtraUserInfoFormat, b: ExtraUserInfoFormat) => {
  return a.orderId - b.orderId;
};

export const modalBodyStyles: CSSProperties = {
  height: '90vh',
  overflowY: 'scroll',
};

export const getPlanNames = (
  subscriptionPlans: SubscriptionPlan[],
  userPlans: { id: string; expiration: number }[]
) => {
  const planIds = userPlans.map((userPlan) => userPlan.id);
  const names = subscriptionPlans.reduce((planNames: string[], plan) => {
    const isIncludedPlanId = planIds.includes(plan._id);
    if (isIncludedPlanId) {
      planNames.push(plan.planName);
    }
    return planNames;
  }, []);

  return names;
};

export const getHouseholdNumberOptions = (
  communityGroups: CommunityGroups[],
  communityName: string
) => {
  let options: Option[] = [];
  const result = communityGroups.find(
    (community) => community.name === communityName
  );
  if (result) {
    options = generateHouseHoldNumberCascader(result.householdNumbers);
  }

  return options;
};

export const getUserSubscriptionPlans = (
  editingTableRecord: TableRecord | undefined,
  subscriptionPlans: SubscriptionPlan[]
) => {
  if (editingTableRecord?.subscriptionPlans) {
    const planNames = getPlanNames(
      subscriptionPlans,
      editingTableRecord?.subscriptionPlans
    );
    return planNames.join('/');
  }
  return [];
};

export const getCustomInformation = (
  extraUserInfoFormat: (Pick<CommunityData, 'extraUserInfoFormat'> & {
    _id: string;
  })[]
) => {
  const customInformation = extraUserInfoFormat[0]?.extraUserInfoFormat
    .filter((question) => {
      return question.isDefault === false;
    })
    .sort(sortColumns);
  if (customInformation === undefined) {
    return [];
  }
  return customInformation;
};

export const getDataSource = (
  users: (User &
    MembershipInformation & {
      _id: string;
    })[],
  searchValue: string
) => {
  let dataSource = getMemberInfos(users);

  if (searchValue) {
    dataSource = dataSource.filter((memberInfo) => {
      return memberInfo.phoneNumber?.includes(searchValue);
    });
  }

  return dataSource;
};

export const emailValidator = (value: string) => {
  if (!value) {
    return Promise.reject(new Error('請輸入電子郵件'));
  }
  const pattern =
    /^(?!\.)(?!.*\.\.)([A-Z0-9_+-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;
  const result = pattern.test(value);
  return result
    ? Promise.resolve()
    : Promise.reject(new Error('請輸入正確的email格式'));
};
